import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { Table, Icon, notification, Row, Col } from "antd";
import moment from "moment";
import ButtonStandard from "../../components/utils/button";
import { ContainerSlaughterSchedule } from "./styles";
import {
  getColumnSearchProps,
  getColumnDateSearchProps,
  getColumnNumberSearchProps,
} from "../../utils/tableUtils";
import { indexSlaughterSchedule } from "../../services/slaughterScheduleService";
import { numberMask } from "../../services/helpersMethodsService";

const INITIAL_TABLE_STATE = {
  page: 1,
  size: 10,
  sorter: null,
  filters: {},
};

const SORT_FIELD_MAP = {
  picketName: "picket_name",
  lotName: "lot_name",
  contractType: "contract_type",
  cattleProducerName: "cattle_producer_name",
  animalsAmount: "animals_amount",
  gender: "gender",
  breed: "breed",
  entryDate: "entry_date",
  entryWeight: "entry_weight",
  gmdPrev: "gmd_prev",
  weightPrev: "weight_prev",
  stayDays: "stay_days",
  slaughterWeight: "slaughter_weight",
  slaughterDays: "slaughter_days",
  slaughterDate: "slaughter_date",
  projectedWeightEd: "projected_weight_ed",
  saleScenarioName: "sale_scenario_name",
  saleScenarioProcessedDate: "sale_scenario_processed_date",
  saleScenarioSellDate: "sale_scenario_sell_date",
  saleScenarioWeightSell: "sale_scenario_weight_sell",
};

const NUMERIC_FIELDS = new Set([
  "animals_amount",
  "entry_weight",
  "slaughter_weight",
  "gmd_prev",
  "weight_prev",
  "stay_days",
  "slaughter_days",
  "projected_weight_ed",
  "sale_scenario_weight_sell",
]);

const DATE_FIELDS = new Set([
  "entry_date",
  "slaughter_date",
  "sale_scenario_processed_date",
  "sale_scenario_sell_date",
]);

const LIST_FIELDS = new Set(["status", "gender", "contract_type"]);

function buildSearchString(filters) {
  if (!filters || Object.entries(filters).length === 0) return "";
  let search = "";

  const appendPart = (part) => {
    search = search === "" ? part : `${search};${part}`;
  };

  Object.entries(filters).forEach(([field, value]) => {
    if (!value || value.length === 0) return;
    const fieldS = SORT_FIELD_MAP[field] || field;

    if (LIST_FIELDS.has(fieldS)) {
      if (fieldS === "gender" && value.includes("BOTH")) {
        appendPart(`${fieldS} in ('M', 'F')`);
      } else {
        appendPart(`${fieldS} in (${value.map((v) => `'${v}'`).join(", ")})`);
      }
    } else if (NUMERIC_FIELDS.has(fieldS)) {
      const operatorS =
        value[1] === "equal"
          ? "="
          : value[1] === "greaterThan"
            ? ">"
            : value[1] === "greaterOrEqualThan"
              ? ">="
              : value[1] === "lessThan"
                ? "<"
                : value[1] === "lessOrEqualThan"
                  ? "<="
                  : value[1] === "between"
                    ? "between"
                    : "=";
      appendPart(
        operatorS === "between"
          ? `${fieldS} ${operatorS} ${value[0]} and ${value[2]}`
          : `${fieldS} ${operatorS} ${value[0]}`,
      );
    } else if (DATE_FIELDS.has(fieldS)) {
      const operatorS =
        value[1] === "equal"
          ? "="
          : value[1] === "greaterThan"
            ? ">"
            : value[1] === "greaterOrEqualThan"
              ? ">="
              : value[1] === "lessThan"
                ? "<"
                : value[1] === "lessOrEqualThan"
                  ? "<="
                  : "=";
      appendPart(
        `cast(${fieldS} as date) ${operatorS} cast('${moment(value[0]).format(
          "YYYY-MM-DD",
        )}' as date)`,
      );
    } else {
      appendPart(`upper(${fieldS}) like upper('%25${value}%25')`);
    }
  });

  return search;
}

const SlaughterSchedule = () => {
  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const col = translation?.slaughterSchedule?.table?.column || {};
  const t = translation?.slaughterSchedule || {};

  const [tableState, setTableState] = useState(INITIAL_TABLE_STATE);
  const [dataList, setDataList] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);

  const fetchData = useCallback(
    async (state) => {
      setLoading(true);
      try {
        const { page, size, sorter, filters } = state;
        const sortField = sorter?.columnKey
          ? SORT_FIELD_MAP[sorter.columnKey] || sorter.columnKey
          : null;
        const sortPayload = sortField
          ? { field: sortField, order: sorter.order }
          : null;
        const search = buildSearchString(filters);

        const {
          data: { results: response },
        } = await indexSlaughterSchedule({
          groupId,
          farmId,
          page,
          size,
          sorter: sortPayload,
          filters: search,
        });

        setDataList(response?.content || []);
        setTotalElements(response?.totalElements || 0);
      } catch {
        notification.error({ message: t.errorLoading });
      } finally {
        setLoading(false);
      }
    },
    [groupId, farmId], // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    fetchData(tableState);
  }, [fetchData, tableState]);

  const handleTableChange = useCallback((pagination, filters, sorter) => {
    setTableState({
      page: pagination.current,
      size: pagination.pageSize,
      sorter: sorter?.columnKey ? sorter : null,
      filters: filters || {},
    });
  }, []);

  const handleSearch = (selectedKeys, confirm) => confirm();
  const handleReset = (clearFilters) => clearFilters();

  // const handleOpenDrawer = (record) => {
  //   setSelectedRecord(record);
  //   setDrawerVisible(true);
  // };

  // const handleCloseDrawer = () => {
  //   setDrawerVisible(false);
  //   setSelectedRecord(null);
  // };

  const statusFilters = [
    { text: col.statusOptions?.active, value: "A" },
    { text: col.statusOptions?.inactive, value: "I" },
    { text: col.statusOptions?.sold, value: "S" },
  ];

  const typeFilters = [
    { text: col.contractTypeOptions?.daily, value: "daily" },
    {
      text: col.contractTypeOptions?.dryMatterConsume,
      value: "dryMatterConsume",
    },
    {
      text: col.contractTypeOptions?.arrobaProduction,
      value: "arrobaProduction",
    },
    { text: col.contractTypeOptions?.partnership, value: "partnership" },
    { text: col.contractTypeOptions?.own, value: "own" },
  ];

  const genderFilters = [
    { text: col.genderOptions?.male, value: "M" },
    { text: col.genderOptions?.female, value: "F" },
    { text: col.genderOptions?.both, value: "BOTH" },
  ];

  const columns = [
    {
      title: col.status,
      fixed: true,
      dataIndex: "status",
      key: "status",
      width: 100,
      filters: statusFilters,
      filterMultiple: true,
      render: (v) =>
        ({
          A: col.statusOptions?.active,
          I: col.statusOptions?.inactive,
          S: col.statusOptions?.sold,
        })[v] ||
        v ||
        "-",
    },
    {
      title: col.picketName,
      fixed: true,
      dataIndex: "picketName",
      key: "picketName",
      width: 150,
      sorter: true,
      sortOrder:
        tableState.sorter?.columnKey === "picketName"
          ? tableState.sorter.order
          : null,
      ...getColumnSearchProps("picketName", handleReset, handleSearch),
      render: (v, record) => <span>{v || "-"}</span>,
    },
    {
      title: col.lotName,
      fixed: true,
      dataIndex: "lotName",
      key: "lotName",
      width: 100,
      sorter: true,
      sortOrder:
        tableState.sorter?.columnKey === "lotName"
          ? tableState.sorter.order
          : null,
      ...getColumnSearchProps("lotName", handleReset, handleSearch),
      render: (v) => v || "-",
    },
    {
      title: col.type,
      dataIndex: "contractType",
      key: "contractType",
      width: 130,
      sorter: true,
      sortOrder:
        tableState.sorter?.columnKey === "contractType"
          ? tableState.sorter.order
          : null,
      filters: typeFilters,
      filterMultiple: true,
      render: (v) => col.contractTypeOptions?.[v] || "-",
    },
    {
      title: col.producerPartner,
      dataIndex: "cattleProducerName",
      key: "cattleProducerName",
      width: 180,
      sorter: true,
      sortOrder:
        tableState.sorter?.columnKey === "cattleProducerName"
          ? tableState.sorter.order
          : null,
      ...getColumnSearchProps("cattleProducerName", handleReset, handleSearch),
      render: (v) => v || "-",
    },
    {
      title: col.animalsAmount,
      dataIndex: "animalsAmount",
      key: "animalsAmount",
      width: 110,
      align: "center",
      sorter: true,
      sortOrder:
        tableState.sorter?.columnKey === "animalsAmount"
          ? tableState.sorter.order
          : null,
      ...getColumnNumberSearchProps("animalsAmount", handleReset, handleSearch),
      render: (v) => (v != null ? v : "-"),
    },
    {
      title: col.gender,
      dataIndex: "gender",
      key: "gender",
      width: 100,
      filters: genderFilters,
      filterMultiple: false,
      render: (v) =>
        ({
          MALE: col.genderOptions?.male,
          FEMALE: col.genderOptions?.female,
          BOTH: col.genderOptions?.both,
        })[v] ||
        v ||
        "-",
    },
    {
      title: col.breed,
      dataIndex: "breed",
      key: "breed",
      width: 120,
      sorter: true,
      sortOrder:
        tableState.sorter?.columnKey === "breed"
          ? tableState.sorter.order
          : null,
      ...getColumnSearchProps("breed", handleReset, handleSearch),
      render: (v) => v || "-",
    },
    {
      title: col.entryDate,
      dataIndex: "entryDate",
      key: "entryDate",
      width: 130,
      sorter: true,
      sortOrder:
        tableState.sorter?.columnKey === "entryDate"
          ? tableState.sorter.order
          : null,
      ...getColumnDateSearchProps("entryDate", handleReset, handleSearch),
      render: (v) => (v ? moment(v).format("DD/MM/YYYY") : "-"),
    },
    {
      title: col.entryWeight,
      dataIndex: "entryWeight",
      key: "entryWeight",
      width: 120,
      align: "center",
      sorter: true,
      sortOrder:
        tableState.sorter?.columnKey === "entryWeight"
          ? tableState.sorter.order
          : null,
      ...getColumnNumberSearchProps("entryWeight", handleReset, handleSearch),
      render: (v) =>
        v != null ? numberMask(Number(v).toFixed(2), false) : "-",
    },
    {
      title: col.contractForecast?.blockTitle,
      className: "colBordered",
      children: [
        {
          title: col.contractForecast?.forecastAdg,
          className: "colBordered",
          dataIndex: "gmdPrev",
          key: "gmdPrev",
          width: 100,
          align: "center",
          render: (v) =>
            v != null ? numberMask(Number(v).toFixed(2), false) : "-",
        },
        {
          title: col.contractForecast?.currentForecastWeight,
          className: "colBordered",
          dataIndex: "weightPrev",
          key: "weightPrev",
          width: 140,
          align: "center",
          render: (v) =>
            v != null ? numberMask(Number(v).toFixed(2), false) : "-",
        },
        {
          title: col.contractForecast?.stayTime,
          className: "colBordered",
          dataIndex: "stayDays",
          key: "stayDays",
          width: 110,
          align: "center",
          render: (v) => (v != null ? `${v}d` : "-"),
        },
        {
          title: col.contractForecast?.slaughterWeight,
          className: "colBordered",
          dataIndex: "slaughterWeight",
          key: "slaughterWeight",
          width: 110,
          align: "center",
          render: (v) =>
            v != null ? numberMask(Number(v).toFixed(2), false) : "-",
        },
        {
          title: col.contractForecast?.slaughterDays,
          className: "colBordered",
          dataIndex: "slaughterDays",
          key: "slaughterDays",
          width: 110,
          align: "center",
          render: (v) => (v != null ? `${v}d` : "-"),
        },
        {
          title: col.contractForecast?.slaughterDate,
          className: "colBordered",
          dataIndex: "slaughterDate",
          key: "slaughterDate",
          width: 120,
          align: "center",
          render: (v) => (v ? moment(v).format("DD/MM/YYYY") : "-"),
        },
      ],
    },
    {
      title: col.saleScenarioBlock?.blockTitle,
      className: "colBordered",
      children: [
        {
          title: col.saleScenarioBlock?.projectedWeightEd,
          className: "colBordered",
          dataIndex: "projectedWeightEd",
          key: "projectedWeightEd",
          width: 120,
          align: "center",
          render: (v) =>
            v != null ? numberMask(Number(v).toFixed(2), false) : "-",
        },
        {
          title: col.saleScenarioBlock?.saleScenario,
          className: "colBordered",
          dataIndex: "saleScenarioName",
          key: "saleScenarioName",
          width: 140,
          align: "center",
          render: (v) => v || "-",
        },
        {
          title: col.saleScenarioBlock?.cvProcessingDate,
          className: "colBordered",
          dataIndex: "saleScenarioProcessedDate",
          key: "saleScenarioProcessedDate",
          width: 170,
          align: "center",
          render: (v) => (v ? moment(v).format("DD/MM/YYYY") : "-"),
        },
        {
          title: col.saleScenarioBlock?.suggestedSaleDate,
          className: "colBordered",
          dataIndex: "saleScenarioSellDate",
          key: "saleScenarioSellDate",
          width: 160,
          align: "center",
          render: (v) => (v ? moment(v).format("DD/MM/YYYY") : "-"),
        },
        {
          title: col.saleScenarioBlock?.estimatedSaleWeight,
          className: "colBordered",
          dataIndex: "saleScenarioWeightSell",
          key: "saleScenarioWeightSell",
          width: 160,
          align: "center",
          render: (v) =>
            v != null ? numberMask(Number(v).toFixed(2), false) : "-",
        },
      ],
    },
  ];

  return (
    <ContainerSlaughterSchedule>
      {/* Header */}
      <Row type="flex" justify="space-between" className="rowHeader">
        <Col></Col>
        <Col>
          <Row type="flex" gutter={8}>
            <Col>
              <ButtonStandard
                buttonType="typeWithoutBackground"
                onClick={() => fetchData(tableState)}
              >
                <Icon type="reload" />
              </ButtonStandard>
            </Col>
          </Row>
        </Col>
      </Row>
      <Row>
        <Table
          rowKey="id"
          size="small"
          loading={loading}
          dataSource={dataList}
          columns={columns}
          onChange={handleTableChange}
          scroll={{ x: true }}
          pagination={{
            current: tableState.page,
            pageSize: tableState.size,
            total: totalElements,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
            hideOnSinglePage: totalElements < 10,
          }}
        />
      </Row>
    </ContainerSlaughterSchedule>
  );
};

export default SlaughterSchedule;
